import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  AdminService,
  RAGUnembeddedCountResponse,
  RAGProcessResponse,
} from '../../services/admin.service';

@Component({
  selector: 'app-admin-embeddings',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './admin-embeddings.component.html',
  styleUrl: './admin-embeddings.component.css',
})
export class AdminEmbeddingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  unembeddedData: RAGUnembeddedCountResponse | null = null;
  processResult: RAGProcessResponse | null = null;

  loadingCount = false;
  loadingProcess = false;
  error: string | null = null;
  successMessage: string | null = null;

  constructor(private adminService: AdminService, private router: Router) {}

  ngOnInit(): void {
    this.loadUnembeddedCount();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadUnembeddedCount(): void {
    this.loadingCount = true;
    this.error = null;
    this.successMessage = null;

    this.adminService
      .getUnembeddedCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.unembeddedData = data;
          this.loadingCount = false;
        },
        error: (error) => {
          console.error('Error loading unembedded count:', error);
          this.error = error.message || 'Failed to load unembedded count';
          this.loadingCount = false;
        },
      });
  }

  processAllListings(): void {
    if (!this.unembeddedData) {
      this.error = 'Please check unembedded count first';
      return;
    }

    if (!this.unembeddedData.withinSafetyLimit) {
      this.error = `Too many unembedded listings (${this.unembeddedData.totalUnembedded}). Maximum safe limit is ${this.unembeddedData.safetyLimit}.`;
      return;
    }

    if (this.unembeddedData.totalUnembedded === 0) {
      this.successMessage =
        'No unembedded listings found. All listings are already processed!';
      return;
    }

    const confirmed = confirm(
      `Are you sure you want to process ${this.unembeddedData.totalUnembedded} unembedded listings?\n\n` +
        `Estimated cost: ${this.unembeddedData.estimatedCost}\n` +
        `This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    this.loadingProcess = true;
    this.error = null;
    this.successMessage = null;

    this.adminService
      .processUnembeddedListings(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.processResult = result;
          this.successMessage = `Successfully processed ${result.totalProcessed} listings! ${result.message}`;
          this.loadingProcess = false;

          setTimeout(() => {
            this.loadUnembeddedCount();
          }, 1000);
        },
        error: (error) => {
          console.error('Error processing listings:', error);
          this.error = error.message || 'Failed to process unembedded listings';
          this.loadingProcess = false;
        },
      });
  }

  goBack(): void {
    this.router.navigate(['/admin/dashboard']);
  }

  clearMessages(): void {
    this.error = null;
    this.successMessage = null;
  }

  formatCurrency(cost: string): string {
    return cost.replace('$', '');
  }

  getSafetyStatusClass(): string {
    if (!this.unembeddedData) return '';
    return this.unembeddedData.withinSafetyLimit ? 'safe' : 'warning';
  }

  getSafetyStatusText(): string {
    if (!this.unembeddedData) return '';
    return this.unembeddedData.withinSafetyLimit
      ? 'Within Safe Limits'
      : 'Exceeds Safe Limits';
  }

  getProcessButtonText(): string {
    if (this.loadingProcess) return 'Processing...';
    if (!this.unembeddedData) return 'Check Count First';
    if (this.unembeddedData.totalUnembedded === 0)
      return 'No Listings to Process';
    return `Process ${this.unembeddedData.totalUnembedded} Listings`;
  }

  isProcessButtonDisabled(): boolean {
    return (
      this.loadingProcess ||
      this.loadingCount ||
      !this.unembeddedData ||
      this.unembeddedData.totalUnembedded === 0 ||
      !this.unembeddedData.withinSafetyLimit
    );
  }
}
